import { StaysSearchTerms } from '@apps/stays/types';
import { useCallback } from 'react';
import { SearchParamsValue } from '@apps/stays/components/SearchParamsObserver/SearchParamsObserver';
import { AccommodationsConsoleState } from '@apps/accommodations/AccommodationsConsole.types';
import { useHistory, useLocation } from '@shared/core';
import { createQueryParamsUpdater } from '../AccommodationsConsole.utils';
import { format } from 'date-fns';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { PICKED_BY_COUPLE_KEY } from './useListingFilters';

type PartialStateUpdater = (updates: Partial<AccommodationsConsoleState>) => void;

/**
 * Formats date components into a string in the format 'yyyy-MM-dd'.
 *
 * ex. { year: 2025, month: 7, day: 15 } -> '2025-07-15'
 * @param date - An object containing year, month, and day.
 * @return A string representing the date in 'yyyy-MM-dd' format.
 */
const formatDateComponentsToString = (date: { year: number; month: number; day: number }) => {
  return format(
    new Date(
      date.year,
      // must decrement month by 1 since Date constructor expects 0-indexed month
      date.month - 1,
      date.day
    ),
    'yyyy-MM-dd'
  );
};

export const useAccommodationHeaderHandlers = (stateUpdater: PartialStateUpdater, forceUpdateMap: () => void) => {
  const history = useHistory();
  const location = useLocation();
  const handleHeaderSearchSubmit = useEventCallback((value: SearchParamsValue) => {
    const updates: Partial<AccommodationsConsoleState> = {};
    if (value.startDate.valid && value.endDate.valid) {
      const startDate = value.startDate.value;
      const endDate = value.endDate.value;
      updates.searchDates = {
        start: formatDateComponentsToString(startDate),
        end: formatDateComponentsToString(endDate)
      };
    }

    if (value.lat.valid && value.lng.valid) {
      updates.circularRegionSearch = {
        latitude: value.lat.value,
        longitude: value.lng.value,
        radius: 4
      };
    }

    if (value.rooms.valid) {
      updates.occupancies = {
        unitCount: value.rooms.value.length,
        occupancies: value.rooms.value.map((room: { adults: number; childrenAges: number[] }) => ({
          adults: room.adults,
          childrenAges: room.childrenAges
        }))
      };
    }

    if (value.destination.valid) {
      updates.searchLocation = value.destination.value;
    }

    stateUpdater(updates);
  });

  const handleHeaderFormSubmit = useCallback(
    (value: StaysSearchTerms) => {
      // Update URL parameters
      const params = new URLSearchParams(location.search);
      const updateQueryParams = createQueryParamsUpdater(params);
      updateQueryParams(PICKED_BY_COUPLE_KEY, null);

      // Only update URL params if we have valid values
      if (value.destination) {
        updateQueryParams('destination', value.destination);
      }
      if (value.lat && value.lng) {
        updateQueryParams('lat', value.lat.toString());
        updateQueryParams('lng', value.lng.toString());
      }
      if (value.startDate && value.endDate) {
        updateQueryParams('startDate', formatDateComponentsToString(value.startDate));
        updateQueryParams('endDate', formatDateComponentsToString(value.endDate));
      }
      if (value.rooms?.length) {
        updateQueryParams('rooms', value.rooms.map(room => `a${room.adults}${room.childrenAges?.length ? `:c${room.childrenAges.join(':c')}` : ''}`).join(','));
      }

      history.replace({
        pathname: location.pathname,
        search: params.toString(),
        hash: location.hash
      });
      forceUpdateMap();

      // Update state
      handleHeaderSearchSubmit({
        destination: { valid: true, value: value.destination },
        lat: { valid: true, value: value.lat },
        lng: { valid: true, value: value.lng },
        startDate: { valid: true, value: value.startDate },
        endDate: { valid: true, value: value.endDate },
        rooms: { valid: true, value: value.rooms }
      });
    },
    [location.search, location.pathname, location.hash, history, forceUpdateMap, handleHeaderSearchSubmit]
  );

  return {
    handleHeaderSearchSubmit,
    handleHeaderFormSubmit
  };
};
